import { useAuth0 } from '@auth0/auth0-react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import { withStyles } from '@material-ui/core/styles';
import jwtDecode from 'jwt-decode';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import * as auth0Actions from '../../actions/auth0';
import * as globalActions from '../../actions/global';
import graphic from '../../assets/underDevelopment.png';

const styles = () => ({
  dialogPaper: {
    overflow: 'inherit',
    width: '95vw',
    maxWidth: 800,
  },
  dialogActions: {
    justifyContent: 'center',
    flexWrap: 'wrap',
    maxHeight: 100,
    '&> div': {
      maxHeight: 100,
    },
  },
  button: {
    background: '#3d3f72',
    color: '#fff',
    '&:hover': {
      background: '#3d3f72DD',
      color: '#fff',
    },
    marginTop: 10,
    marginBottom: 10,
  },
});

const DialogContent = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing.unit * 2,
  },
}))(MuiDialogContent);

const LoginModal = ({ classes, config }) => {
  const dispatch = useDispatch();
  const { fetchAuth0OrganizationId, loginToUserService } = useMemo(
    () =>
      bindActionCreators(
        {
          fetchAuth0OrganizationId: auth0Actions.fetchAuth0OrganizationId,
          loginToUserService: globalActions.loginToUserService,
        },
        dispatch,
      ),
    [dispatch],
  );
  const auth0 = useAuth0();
  const [isAuthenticatedWithUserService, setIsAuthenticatedWithUserService] =
    useState(false);

  async function getAuth0LoginOptions() {
    try {
      const auth0Office = config?.authenticateOffice
        ?.replace(' ', '')
        ?.toLowerCase();
      if (auth0Office) {
        const organizationId = (
          await fetchAuth0OrganizationId({ organizationName: auth0Office })
        ).results.organizationId;
        console.info(
          `Fetched auth0 organization id ${organizationId} for ${auth0Office}`,
        );

        return {
          organization: organizationId, // puts org in access token
          authorizationParams: { organization: organizationId }, // skips org selection step in universal login
        };
      }

      console.info('No auth0 office found');
      return {};
    } catch (error) {
      console.error(`Error getting auth0 login options: ${error}`);
    }
  }

  const loginToAuth0 = async () => {
    const options = await getAuth0LoginOptions();
    console.log('options', options);
    await auth0.loginWithRedirect(options);
  };

  async function getAuth0Token() {
    try {
      const options = await getAuth0LoginOptions();
      const token = await auth0.getAccessTokenSilently(options);
      const decodedToken = jwtDecode(token);
      if (decodedToken.exp < Date.now() / 1000) {
        throw new Error('Token expired');
      }
      return token;
    } catch (error) {
      console.info(
        `[loginModal.js]: Error getting or decoding auth0 token: ${error}`,
      );
      if (error?.message === 'External interaction required') {
        loginToAuth0();
      }
      throw error;
    }
  }

  async function getTokenFromUserService() {
    try {
      const token = await getAuth0Token();

      if (!token) {
        throw new Error('No token received from Auth0');
      }

      const response = await loginToUserService({ token });
      if (response.error) {
        throw new Error(response.error);
      }

      setIsAuthenticatedWithUserService(true);
    } catch (error) {
      const message = `Cannot log in to user service for ${
        auth0.user?.office?.display_name || 'Unknown Office'
      } - ${error?.message?.toLowerCase()}`;
      console.error(message);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: getTokenFromUserService is recreated each render; re-run only when auth state changes
  useEffect(() => {
    if (auth0.isAuthenticated && !isAuthenticatedWithUserService) {
      getTokenFromUserService();
    }
  }, [auth0.isAuthenticated, isAuthenticatedWithUserService]);

  const { isAuthenticated: isAuthenticatedAuth0, isLoading: isLoadingAuth0 } =
    auth0;

  return (
    <div>
      <Dialog
        classes={{ paper: classes.dialogPaper }}
        open={
          Boolean(config?.authenticateUser) &&
          !isAuthenticatedAuth0 &&
          !isLoadingAuth0
        }
      >
        <DialogContent style={{ display: 'flex' }}>
          <LoginModal.LeftContent>
            <img
              alt='under construction'
              src={graphic}
              style={{ width: '100%' }}
            />
            <span>{config?.betaMessage}</span>
          </LoginModal.LeftContent>
          <LoginModal.RightContent>
            <LoginModal.OfficeTitle>
              {config?.authenticateOffice}
            </LoginModal.OfficeTitle>
            <LoginModal.Header>Tax Transparency Sign-in</LoginModal.Header>
            <LoginModal.Description>
              Public users cannot currently access this site. If you work for
              the Appraisal District, you can log in to the under-construction
              version of this site using the button below.
            </LoginModal.Description>
            <Button
              className={classes.button}
              fullWidth
              type='submit'
              variant='contained'
              onClick={loginToAuth0}
            >
              SIGN IN
            </Button>
            <LoginModal.PoweredBy>
              Website powered by{' '}
              <b style={{ fontWeight: '600' }}>&lt;True Prodigy&gt;</b>
            </LoginModal.PoweredBy>
          </LoginModal.RightContent>
        </DialogContent>
      </Dialog>
    </div>
  );
};

LoginModal.LeftContent = styled.div`
  display: flex;
  flex: 3;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 30px;

  > span {
    text-align: center;
    font-family: HelveticaNeue, Arial, sans-serif;
    font-size: 20px;
  }
`;

LoginModal.RightContent = styled.div`
  display: flex;
  position: relative;
  flex: 2;
  flex-direction: column;
  box-shadow: -10px 4px 7px -10px rgba(0, 0, 0, 0.16);
  font-family: Roboto, Arial, sans-serif;
  min-width: 350px;
  min-height: 450px;
  padding: 20px;
`;

LoginModal.PoweredBy = styled.div`
  position: absolute;
  font-family: Arial, sans-serif;
  font-size: 12px;
  bottom: 0;
  right: 0;
  color: #383c9d;
`;

LoginModal.OfficeTitle = styled.div`
  display: flex;
  justify-content: center;
  font-size: 34px;
  font-weight: 900;
`;

LoginModal.Description = styled.div`
  font-size: 16px;
  margin-bottom: 20px;
`;

LoginModal.Header = styled.div`
  font-size: 24px;
  line-height: 1.33;
  color: #383c9d;
  display: flex;
  margin-bottom: 20px;
  justify-content: center;
`;

LoginModal.Error = styled.div`
  color: red;
  font-family: Roboto, arial, sans-serif;
  font-size: 12px;
`;

export default withStyles(styles)(LoginModal);
